import { apiService, API_BASE_URL } from "../services/api";

export type LessonNoteSource = "MANUAL" | "AI_GENERATED" | "AI_ASSISTED" | "PDF_UPLOAD";

/** A note is PDF-backed when it was created from an upload OR simply has a stored file —
 *  the file is the authoritative signal, so a note whose PDF exists never falls back to
 *  the rich editor / HTML reader showing its extracted text as if it were the note. */
export const isPdfBackedNote = (note: {
  source?: string | null;
  file_path?: string | null;
  file_name?: string | null;
  page_count?: number | null;
}): boolean => note.source === "PDF_UPLOAD" || !!note.file_path || !!note.file_name || !!note.page_count;

/** Where a note sits in e-learning. Null when it isn't on any course — being PUBLISHED and
 *  shared is not the same as being part of the course students work through. */
export interface LessonNotePlacement {
  item_id: number;
  is_published: boolean;
  section_id: number;
  section_title: string;
  section_status: "HIDDEN" | "SCHEDULED" | "PUBLISHED";
  section_unlock_at: string | null;
  course_id: number;
  course_title: string;
  course_status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
}

export type NoteReachState = "LIVE" | "SCHEDULED" | "BLOCKED" | "OFF_COURSE";
export type NoteReachFix = "publish_note" | "place" | "show_item" | "publish_week" | "publish_course";

/** Whether students can actually open the note on its course, and what's in the way if not
 *  (server: services/elearning/noteReach.ts). */
export interface NoteReach {
  state: NoteReachState;
  opens_at: string | null;
  blocker: string | null;
  steps: {
    key: "note" | "placed" | "item" | "week" | "course";
    ok: boolean;
    label: string;
    detail?: string;
    fix?: NoteReachFix;
  }[];
}

/** The course an unplaced note could be added to, when one exists for its subject + class group. */
export interface LessonNoteCourseTarget {
  course_id: number;
  title: string;
  status: string;
}

/** One row per subject the teacher can write notes for, for the subject-first landing step.
 *  Driven by their subject assignments, so a subject with no notes yet still appears. */
export interface LessonNoteSubjectSummary {
  subject_id: number;
  subject_name: string;
  subject_code: string | null;
  /** False for a subject that only exists through older notes (no current assignment). */
  is_assigned: boolean;
  note_count: number;
  published_count: number;
  draft_count: number;
  on_course_count: number;
  /** Notes students can open on the course today (placed AND note, item, week, course all open). */
  live_count: number;
  /** The teacher's e-learning course for this subject, and how many of its weeks are live. */
  course_id: number | null;
  weeks_total: number;
  weeks_live: number;
  /** Null when the subject has no notes yet. */
  last_updated: string | null;
  class_group_names: string | null;
}

export interface LessonNoteSummary {
  note_id: number;
  subject_id: number;
  subject_name: string;
  class_group_id: number | null;
  class_group_name: string | null;
  scheme_entry_id: number | null;
  title: string;
  status: "DRAFT" | "PUBLISHED";
  source: LessonNoteSource;
  /** PDF_UPLOAD notes only. */
  file_name: string | null;
  page_count: number | null;
  /** Performance criteria this note covers (see curriculum_context on the detail). */
  criteria_ids: number[];
  /** Active (unexpired) shares. 0 means no student can see this note, published or not. */
  share_count: number;
  created_at: string;
  updated_at: string;
  /** Present on the teacher's own list; null when the note isn't on a course. */
  elearning?: LessonNotePlacement | null;
  /** Only set when `elearning` is null and a course does exist to place the note into. */
  course_target?: LessonNoteCourseTarget | null;
  reach?: NoteReach;
}

export interface CurriculumCriterion {
  criteria_id: number;
  criteria_number: string;
  description: string;
}

/** One Learning Outcome a note covers, with only the criteria that are in scope. */
export interface CurriculumOutcome {
  competency_id: number;
  element_number: number;
  title: string;
  description: string | null;
  indicative_content: string | null;
  learning_hours: number | null;
  /** Total criteria under the outcome, so the UI can tell "whole outcome" from "3 of 5". */
  total_criteria: number;
  criteria: CurriculumCriterion[];
}

export interface LessonNoteDetail {
  note_id: number;
  user_id: number;
  subject_id: number;
  class_group_id: number | null;
  scheme_entry_id: number | null;
  academic_term_id: number | null;
  title: string;
  content_json: any;
  content_html: string | null;
  status: "DRAFT" | "PUBLISHED";
  source: LessonNoteSource;
  /** PDF_UPLOAD notes only — the stored file's display metadata. content_html then holds
   *  the text extracted from it (feeds the student AI tutor), not editable HTML. */
  file_path: string | null;
  file_name: string | null;
  file_size: number | null;
  page_count: number | null;
  created_at: string;
  updated_at: string;
  /** Active (unexpired) shares. 0 means no student can see this note, published or not. */
  share_count: number;
  /** Legacy: notes created before curriculum coverage were anchored to one Scheme of Work week. */
  scheme_context: {
    entry: {
      entry_id: number;
      week_number: string;
      topic: string;
      sub_topic: string;
      objective: string;
    };
    criteria: { criteria_id: number; criteria_number: string; description: string }[];
  } | null;
  curriculum_context: { outcomes: CurriculumOutcome[]; criteria_ids: number[] } | null;
  /** Names for the edit form — the note's own subject/class even if no longer assigned. */
  subject_name: string | null;
  class_group_name: string | null;
  /** Where the note sits on e-learning; while set, its subject and class can't change. */
  elearning: LessonNotePlacement | null;
}

export interface LessonNoteVersion {
  version_id: number;
  created_by: "USER" | "AI";
  prompt_text: string | null;
  created_at: string;
}

export interface LessonNoteShare {
  share_id: number;
  note_id: number;
  filter_type: "class_group" | "subject_enrolled" | "specific_students";
  filter_ids: number[];
  permission: "VIEW";
  expires_at: string | null;
  created_at: string;
}

export interface AINoteGenerationStatus {
  status: "loading" | "analyzing" | "structuring" | "saving" | "done" | "error";
  stepIndex: number;
  totalSteps: number;
  message: string;
  noteId?: number;
  error?: string;
  /** Which AI provider answered (gemini/groq/deepseek/openrouter/glm) — informational, no UI surfaces this yet. */
  providerUsed?: string;
  /** Which AI wrote the note (School AI or the teacher's own key). */
  ai?: import("./myAi").AiRouteMeta | null;
}

export interface PromptPreset {
  preset_id: number;
  label: string;
  prompt_text: string;
  created_at: string;
}

export interface SharedNoteSummary {
  note_id: number;
  title: string;
  subject_name: string;
  teacher_name: string;
  /** The teacher's MIS user id (their photo is looked up from it). */
  teacher_id?: number;
  updated_at: string;
  source: LessonNoteSource;
  page_count: number | null;
  /** Plain-text preview of the note body, computed server-side so the list stays light. */
  excerpt: string;
  word_count: number;
  reading_minutes: number;
  /**
   * Where this note sits on a course the student is a member of, when it does.
   * Reading it there counts towards the week; reading it in the standalone
   * reader does not. Null for a note that was only ever shared.
   */
  placement: StudentNotePlacement | null;
}

/** A note's place on a course the student belongs to, with their own progress on it. */
export interface StudentNotePlacement {
  course_id: number;
  course_title: string;
  item_id: number;
  section_id: number;
  section_title: string;
  progress: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
}

export interface NoteAskAnswer {
  answer_html: string;
  key_points: string[];
  follow_ups: string[];
  /** false when the note itself doesn't cover the question — the UI flags this to the student. */
  grounded: boolean;
  provider_used: string;
  /** Which AI answered (School AI or the person's own key). */
  ai?: import("./myAi").AiRouteMeta | null;
}

export interface SharedNoteDetail {
  note_id: number;
  title: string;
  content_html: string;
  status: "PUBLISHED";
  source: LessonNoteSource;
  file_name: string | null;
  page_count: number | null;
  subject_id: number;
  subject_name: string;
  updated_at: string;
  /** Credited in the reader header, so opening a note keeps the facts the card showed. */
  teacher_name: string;
  /** The teacher's MIS user id (their photo is looked up from it). */
  teacher_id?: number;
  word_count: number;
  reading_minutes: number;
  /** Set when the note is coursework for this student — the reader points back to the week. */
  placement?: StudentNotePlacement | null;
}

export const lessonNotesApi = {
  list: (filters?: { subject_id?: number; class_group_id?: number; academic_term_id?: number; status?: string }) =>
    apiService.get<{ data: LessonNoteSummary[] }>("/lesson-notes", { params: filters }),

  /** Subjects the teacher has notes for, with counts — drives the "pick a subject first" step. */
  subjects: (filters?: { academic_term_id?: number; academic_year_id?: number }) =>
    apiService.get<{ data: LessonNoteSubjectSummary[] }>("/lesson-notes/subjects", { params: filters }),

  create: (data: {
    subject_id: number;
    class_group_id?: number;
    scheme_entry_id?: number;
    academic_term_id?: number;
    title: string;
    criteria_ids?: number[];
  }) => apiService.post<{ data: { note_id: number } }>("/lesson-notes", data),

  /** Third creation path: a PDF prepared elsewhere becomes a read-only DRAFT note. */
  createFromPdf: (
    data: {
      subject_id: number;
      class_group_id?: number;
      scheme_entry_id?: number;
      academic_term_id?: number;
      title: string;
      criteria_ids?: number[];
    },
    file: File,
    onProgress?: (fraction: number) => void,
  ) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      // Arrays travel as JSON in a multipart field; the backend parses either shape.
      form.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
    });
    form.append("file", file);
    return apiService.post<{ data: { note_id: number; page_count: number; is_textless: boolean } }>(
      "/lesson-notes/upload-pdf",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e: { loaded: number; total?: number }) => onProgress?.(e.total ? e.loaded / e.total : 0),
      },
    );
  },

  replacePdf: (id: number, file: File, onProgress?: (fraction: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return apiService.post<{ data: { page_count: number; is_textless: boolean } }>(
      `/lesson-notes/${id}/pdf`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e: { loaded: number; total?: number }) => onProgress?.(e.total ? e.loaded / e.total : 0),
      },
    );
  },

  /** The stored PDF itself — works for the owning teacher and for students the note reaches.
   *  `version` (e.g. the note's updated_at) makes a replaced file a new URL: the offline
   *  service worker serves this path stale-while-revalidate, and a replacement keeps the
   *  same storage path, so without it the old file would show once more. */
  getPdfBlob: (id: number, version?: string) =>
    apiService.get<Blob>(`/lesson-notes/${id}/pdf/raw`, {
      responseType: "blob",
      params: version ? { v: version } : undefined,
    }),

  get: (id: number) => apiService.get<{ data: LessonNoteDetail }>(`/lesson-notes/${id}`),

  update: (
    id: number,
    data: Partial<{
      title: string;
      content_json: any;
      content_html: string;
      status: "DRAFT" | "PUBLISHED";
      snapshot_prompt: string;
      criteria_ids: number[];
      /** Moving a note: refused (409) while it is on an e-learning course. */
      subject_id: number;
      class_group_id: number | null;
    }>,
  ) => apiService.patch(`/lesson-notes/${id}`, data),

  /** Also removes the note from every e-learning course it was placed on. */
  remove: (id: number) =>
    apiService.delete<{ data: { removed_course_items: number } }>(`/lesson-notes/${id}`),

  uploadImage: (id: number, file: File) => {
    const form = new FormData();
    form.append("image", file);
    return apiService.post<{ data: { image_id: number; url: string } }>(
      `/lesson-notes/${id}/images`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  },

  startAIGenerate: (data: {
    entry_id?: number;
    competency_id?: number;
    /** Performance criteria to ground the notes in -- may span several Learning Outcomes. */
    criteria_ids?: number[];
    subject_id?: number;
    class_group_id?: number;
    academic_term_id?: number;
    extra_instructions?: string;
    /** My AI: "" / "school" / "<credentialId>:<model>" for this request only. */
    aiRoute?: string;
    /** "Open in my AI" / "Paste the answer" (HandoffControl). */
    aiHandoff?: unknown;
  }) =>
    apiService.post<{ data: { jobId: string } }>("/lesson-notes/ai-generate", data),

  getAIGenerateStatus: (jobId: string) =>
    apiService.get<{ data: AINoteGenerationStatus }>(`/lesson-notes/ai-generate/${jobId}/status`),

  proposeAIEdit: (id: number, data: { instruction: string; selection_html?: string; whole_note_html?: string; aiRoute?: string; aiHandoff?: unknown }) =>
    apiService.post<{ data: { html: string; ai?: import("./myAi").AiRouteMeta | null } }>(`/lesson-notes/${id}/ai-edit`, data),

  listVersions: (id: number) => apiService.get<{ data: LessonNoteVersion[] }>(`/lesson-notes/${id}/versions`),

  restoreVersion: (id: number, versionId: number) =>
    apiService.post<{ data: { content_json: any } }>(`/lesson-notes/${id}/versions/${versionId}/restore`),

  share: (id: number, data: { filter_type: string; filter_ids: number[]; expires_at?: string | null }) =>
    apiService.post<{ data: { share_id: number } }>(`/lesson-notes/${id}/share`, data),

  listShares: (id: number) => apiService.get<{ data: LessonNoteShare[] }>(`/lesson-notes/${id}/share`),

  revokeShare: (id: number, shareId: number) =>
    apiService.delete(`/lesson-notes/${id}/share/${shareId}`),

  sharedWithMe: () => apiService.get<{ data: SharedNoteSummary[] }>("/lesson-notes/shared-with-me"),

  getShared: (id: number) => apiService.get<{ data: SharedNoteDetail }>(`/lesson-notes/shared-with-me/${id}`),

  askAboutShared: (
    id: number,
    data: {
      question: string;
      selection_text?: string;
      mode?: "explain" | "simplify" | "example" | "define" | "quiz";
      history?: { role: "user" | "assistant"; content: string }[];
      aiRoute?: string;
      aiHandoff?: unknown;
    },
  ) => apiService.post<{ data: NoteAskAnswer }>(`/lesson-notes/shared-with-me/${id}/ask`, data),

  exportPdf: (id: number) =>
    apiService.get<Blob>(`/lesson-notes/${id}/export-pdf`, { responseType: "blob" }),

  listPromptPresets: () => apiService.get<{ data: PromptPreset[] }>("/lesson-notes/prompt-presets"),

  savePromptPreset: (data: { label: string; prompt_text: string }) =>
    apiService.post<{ data: PromptPreset }>("/lesson-notes/prompt-presets", data),

  deletePromptPreset: (presetId: number) =>
    apiService.delete(`/lesson-notes/prompt-presets/${presetId}`),
};

/** Images are rendered via plain <img> tags, which can't carry an Authorization
 * header or a cross-site cookie — the backend accepts the same JWT as a query
 * param on this one GET endpoint instead (see backend middleware/auth.ts). */
export const lessonNoteImageUrl = (imageId: number) => {
  const token = localStorage.getItem("token");
  return `${API_BASE_URL}/lesson-notes/images/${imageId}/raw${token ? `?token=${encodeURIComponent(token)}` : ""}`;
};
